/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useCallback, useEffect, useState } from "react";
import { BlogTagFragment, CosmosVideoEmbedFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { IFrame } from "./IFrame.atom";
import { CosmosPlayIcon, CosmosTagProps, CosmosTagsAndAttribution } from "@components/elements";
import { useAssetOptUrl, useIsMobile } from "@components/hooks";
import { EmbedVariantEnum, extractYouTubeVideoId, getFinalIframeSrc, getYouTubeThumbnailUrl } from "./embed.utils";

export interface CosmosEmbedProps extends Omit<CosmosVideoEmbedFragment, "__typename"> {
	className?: string;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	attributionClassName?: string;
}

// Prioritizes user-provided overlay images, falling back to YouTube thumbnails when available
const useYouTubeThumbnail = (
	embedSrc: string,
	overlayOptUrl: string | undefined,
	embedVariant: EmbedVariantEnum,
): string => {
	const [overlayImageUrl, setOverlayImageUrl] = useState("");

	useEffect(() => {
		if (overlayOptUrl) {
			setOverlayImageUrl(overlayOptUrl);
			return;
		}
		if (embedVariant === EmbedVariantEnum.YouTube) {
			const videoId = extractYouTubeVideoId(embedSrc);
			if (videoId) {
				void getYouTubeThumbnailUrl(videoId).then(setOverlayImageUrl);
			}
		}
	}, [embedSrc, overlayOptUrl, embedVariant]);

	return overlayImageUrl;
};

const useEmbedPlayHandler = (
	embedVariant: EmbedVariantEnum,
	finalIframeSrc: string,
	disableOverlay: boolean,
): { isPlayClicked: boolean; handlePlayClick: () => void } => {
	const [isPlayClicked, setIsPlayClicked] = useState(disableOverlay);

	const handlePlayClick = useCallback((): void => {
		// Force prohibited iframe source URLs to open in a new tab
		if (embedVariant === EmbedVariantEnum.Prohibited) {
			window.open(finalIframeSrc, "_blank");
			return;
		}
		setIsPlayClicked(true);
	}, [embedVariant, finalIframeSrc]);

	return { isPlayClicked, handlePlayClick };
};

const styles = css({
	position: "relative",
	overflow: "hidden",
	aspectRatio: "16/9",
	borderRadius: "eds.dimension.24",
});

export const CosmosEmbed = ({
	className,
	iframeSrc,
	playButtonAriaLabel,
	overlayImage,
	disableOverlay,
	tags,
	attribution,
	attributionClassName,
}: CosmosEmbedProps): React.JSX.Element | null => {
	const isMobile = useIsMobile();
	const shouldMute = (!isMobile && disableOverlay) || false;
	const overlayOptUrl = useAssetOptUrl(overlayImage);

	let embedVariant = EmbedVariantEnum.Miscellaneous;
	if (iframeSrc) {
		if (/(bilibili\.com|twitch\.tv)/g.test(iframeSrc)) embedVariant = EmbedVariantEnum.Prohibited;
		if (/(player\.vimeo\.com)/g.test(iframeSrc)) embedVariant = EmbedVariantEnum.Vimeo;
		if (/(youtu\.be|youtube\.com|youtube-nocookie\.com)/g.test(iframeSrc)) embedVariant = EmbedVariantEnum.YouTube;
	}

	const finalIframeSrc = getFinalIframeSrc(embedVariant, iframeSrc, shouldMute);
	const overlayImageUrl = useYouTubeThumbnail(finalIframeSrc, overlayOptUrl, embedVariant);
	const { isPlayClicked, handlePlayClick } = useEmbedPlayHandler(embedVariant, finalIframeSrc, disableOverlay || false);

	if (!iframeSrc) return null;

	return (
		<div className={cx("cosmos-video-embed", styles, className)}>
			{isPlayClicked ? (
				<IFrame iframeSrc={finalIframeSrc} title={playButtonAriaLabel} />
			) : (
				<button
					className={css({
						cursor: "pointer",
						width: "100%",
						height: "100%",
						position: "absolute",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						bgRepeat: "no-repeat",
						bgPosition: "top",
						bgSize: "cover",
						bgBlendMode: "darken",
						bgColor: "eds.background.backdrop.regular",
						"&:hover .cosmos-play-icon": {
							background: "eds.fill.secondary.hover",
						},
						"&:focus-visible": {
							outlineColor: "token(colors.eds.border.focus)",
							outlineOffset: "-eds.dimension.3",
							borderRadius: "eds.borderRadius.16",
						},
					})}
					style={{ backgroundImage: `url(${overlayImageUrl})` }}
					aria-label={playButtonAriaLabel || "Play Video"}
					onClick={handlePlayClick}
				>
					<CosmosTagsAndAttribution className={attributionClassName} tags={tags} attribution={attribution}>
						<CosmosPlayIcon size="lg" />
					</CosmosTagsAndAttribution>
				</button>
			)}
		</div>
	);
};
